import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, UploadCloud, FileText, Trash2, Download, ShieldCheck, AlertTriangle, ChevronDown } from "lucide-react";

const GUIDES = [
  {
    platform: "MetaTrader 4 / 5",
    steps: [
      "Onglet « Terminal » (Ctrl+T) → « Historique du compte »",
      "Clic droit → « Tout l'historique » pour afficher toutes les périodes",
      "Clic droit → « Rapport » (HTML) ou « Exporter » CSV selon votre broker",
      "Si l'export CSV est bloqué par votre broker : ouvrez le rapport HTML et collez son contenu ci-dessous — nous le parsons directement",
    ],
  },
  {
    platform: "cTrader",
    steps: [
      "Onglet « History » en bas de la plateforme",
      "Clic droit dans le tableau → « Export to CSV »",
      "Glissez-déposez le fichier CSV ci-dessous",
    ],
  },
  {
    platform: "Interactive Brokers",
    steps: [
      "Portail Client → « Performance & Reports » → « Flex Queries » (ou « Relevés d'activité »)",
      "Créez une Flex Query de type « Trades » au format CSV, avec au minimum les colonnes Symbol et Realized P/L (FifoPnlRealized)",
      "Exécutez la requête, téléchargez le CSV et glissez-déposez-le ci-dessous — les intitulés IBKR (Symbol, Buy/Sell, Quantity, TradePrice, Realized P/L, Date/Time) sont reconnus automatiquement",
    ],
  },
  {
    platform: "NinjaTrader",
    steps: [
      "Onglet « Trade Performance » (Control Center → New → Trade Performance)",
      "Sélectionnez la période, affichez l'onglet « Trades », puis clic droit → « Export » (CSV)",
      "Les intitulés NinjaTrader (Instrument, Market pos., Qty, Entry price, Exit price, Profit) sont reconnus automatiquement",
    ],
  },
  {
    platform: "Sierra Chart",
    steps: [
      "Menu « Trade » → « Trade Activity Log » → onglet « Trades »",
      "Bouton « Export » (fichier texte/CSV délimité par tabulations — accepté tel quel)",
      "Les intitulés Sierra Chart (Symbol, Trade Type, Trade Quantity, Entry Price, Exit Price, Profit/Loss (P/L)) sont reconnus automatiquement",
    ],
  },
  {
    platform: "TradingView",
    steps: [
      "TradingView n'exécute pas nativement — l'historique réel est chez votre broker connecté (OANDA, IBKR…)",
      "Exportez l'historique depuis le portail de votre broker (format CSV)",
      "Notre détection automatique mappe les colonnes standard : date, symbole, sens, volume, prix, résultat",
    ],
  },
];

const TEMPLATE_CSV = [
  "symbol,side,volume,entry,exit,pnl,open time,close time",
  "EURUSD,buy,0.10,1.0842,1.0871,29.00,2026-05-02 08:15,2026-05-02 11:40",
  "NAS100,sell,0.50,18250.5,18190.0,30.25,2026-05-03 14:05,2026-05-03 15:22",
  "XAUUSD,buy,0.20,2315.40,2311.10,-8.60,2026-05-04 09:30,2026-05-04 10:10",
].join("\n");

export default function TradeImport() {
  const [content, setContent] = useState("");
  const [filename, setFilename] = useState(null);
  const [balance, setBalance] = useState(10000);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [result, setResult] = useState(null);
  const [imported, setImported] = useState(null);
  const [quota, setQuota] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [openGuide, setOpenGuide] = useState(null);
  const fileRef = useRef(null);

  const refresh = () => {
    axios.get(`${API}/trades/imported`).then((r) => setImported(r.data)).catch(() => {});
    axios.get(`${API}/reporting/report-quota`).then((r) => setQuota(r.data)).catch(() => {});
  };
  useEffect(refresh, []);

  const readFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => { setContent(e.target.result); setFilename(file.name); setPreview(null); };
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "anatradex_modele_import.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const doPreview = async () => {
    if (!content.trim()) { toast.error("Collez ou déposez d'abord votre historique"); return; }
    setPreviewing(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/trades/import/preview`, {
        content, filename, account_balance: Number(balance) || 10000,
      });
      setPreview(r.data);
      if (r.data.n_trades === 0) toast.error("Aucun trade exploitable détecté — vérifiez les colonnes");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'analyse du fichier");
    } finally { setPreviewing(false); }
  };

  const doImport = async () => {
    setImporting(true);
    try {
      const r = await axios.post(`${API}/trades/import`, {
        content, filename, account_balance: Number(balance) || 10000,
      });
      setResult(r.data);
      setPreview(null);
      setContent(""); setFilename(null);
      const nSkipped = (r.data.skipped?.non_trade || 0) + (r.data.skipped?.invalid || 0);
      toast.success(`${r.data.n_trades} trades importés (${r.data.format})${nSkipped > 0 ? ` — ${nSkipped} ligne(s) ignorée(s)` : ""}`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'import");
    } finally { setImporting(false); }
  };

  const clearAll = async () => {
    if (!window.confirm("Supprimer tous vos trades importés ?")) return;
    try {
      await axios.delete(`${API}/trades/imported`);
      setResult(null);
      toast.success("Trades importés supprimés");
      refresh();
    } catch { toast.error("Erreur de suppression"); }
  };

  const downloadReport = async () => {
    setPdfLoading(true);
    try {
      const r = await axios.get(`${API}/reporting/dd-pack?use_real_trades=true`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "ANATRADEX_Rapport_Analyse_Statistique.pdf"; a.click();
      URL.revokeObjectURL(url);
      refresh();
    } catch (e) {
      if (e.response?.status === 403) {
        const txt = await e.response.data.text?.().catch(() => null);
        let msg = "Quota Free atteint : 1 rapport/mois. Passez Pro pour des rapports illimités.";
        try { msg = JSON.parse(txt)?.detail || msg; } catch { /* ignore */ }
        toast.error(msg);
      } else { toast.error("Erreur de génération du rapport"); }
    } finally { setPdfLoading(false); }
  };

  return (
    <div className="p-6 space-y-6 max-w-4xl" data-testid="trade-import-page">
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight">Importer mes trades</h1>
        <p className="text-white/50 text-sm mt-1">
          MT4 / MT5 (CSV ou rapport HTML) · cTrader · Interactive Brokers · NinjaTrader · Sierra Chart · CSV générique —
          détection automatique du format, zéro code requis. Vos trades alimentent votre Rapport d&apos;Analyse Statistique (PBO, DSR, permutation).
        </p>
      </div>

      {/* Dropzone + paste */}
      <div
        className="panel p-5 border-2 border-dashed border-white/15 hover:border-accent-blue/40 transition-colors"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); readFile(e.dataTransfer.files?.[0]); }}
        data-testid="import-dropzone"
      >
        <div className="flex items-center gap-3 mb-3">
          <UploadCloud size={20} className="text-accent-blue" />
          <div className="text-sm">
            Glissez-déposez votre fichier <b>.csv</b> ou <b>.htm/.html</b>, ou{" "}
            <button className="text-accent-blue underline" onClick={() => fileRef.current?.click()} data-testid="import-browse-btn">
              parcourez
            </button>
            {filename && <span className="ml-2 text-up text-xs">✓ {filename}</span>}
          </div>
          <input ref={fileRef} type="file" accept=".csv,.htm,.html,.txt" className="hidden"
                 onChange={(e) => readFile(e.target.files?.[0])} data-testid="import-file-input" />
        </div>
        <textarea
          className="field w-full h-36 font-mono text-xs"
          placeholder="…ou collez ici le contenu de votre export CSV / rapport HTML MT4"
          value={content}
          onChange={(e) => { setContent(e.target.value); setPreview(null); }}
          data-testid="import-content-textarea"
        />
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-accent-blue/25 bg-accent-blue/8 px-3 py-2 text-xs text-white/70" data-testid="import-required-columns-note">
          <AlertTriangle size={13} className="text-accent-blue shrink-0 mt-0.5" />
          <span>
            <b className="text-white">2 colonnes obligatoires : symbole + profit</b> (résultat du trade). Les autres
            (sens, volume, prix, dates) sont optionnelles mais recommandées. Les lignes sans symbole ou sans profit
            numérique sont ignorées — le nombre exact vous sera indiqué après l&apos;import.{" "}
            <button
              className="text-accent-blue underline"
              onClick={downloadTemplate}
              data-testid="download-template-btn"
            >
              Télécharger le CSV modèle
            </button>
          </span>
        </div>
        <div className="mt-3 flex flex-wrap items-end gap-4">
          <div>
            <label className="label">Solde du compte (pour normaliser les rendements)</label>
            <input type="number" className="field w-40" value={balance}
                   onChange={(e) => setBalance(e.target.value)} data-testid="import-balance-input" />
          </div>
          <button className="btn btn-primary" onClick={doPreview} disabled={previewing || importing} data-testid="import-submit-btn">
            {previewing ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Analyser le fichier
          </button>
        </div>
      </div>

      {/* Preview before confirm */}
      {preview && (
        <div className="panel p-5 border border-accent-blue/30" data-testid="import-preview">
          <div className="flex items-center gap-2 text-accent-blue font-semibold text-sm mb-1">
            <FileText size={16} /> Aperçu avant import — format détecté : {preview.format}
          </div>
          <div className="text-xs text-white/60 mb-3">
            <b className="text-white">{preview.n_trades} trade(s)</b> détecté(s) sur {preview.n_rows} ligne(s) de données
            {(preview.skipped?.non_trade || 0) + (preview.skipped?.invalid || 0) > 0 && (
              <span className="text-warn" data-testid="preview-skipped-info">
                {" — "}{(preview.skipped.non_trade || 0) + (preview.skipped.invalid || 0)} ignorée(s)
                {preview.skipped.non_trade > 0 && ` (${preview.skipped.non_trade} non-trade`}
                {preview.skipped.non_trade > 0 && preview.skipped.invalid > 0 && `, ${preview.skipped.invalid} invalide(s))`}
                {preview.skipped.non_trade > 0 && preview.skipped.invalid === 0 && ")"}
                {preview.skipped.non_trade === 0 && preview.skipped.invalid > 0 && ` (${preview.skipped.invalid} invalide(s))`}
              </span>
            )}
            {preview.symbols?.length > 0 && <span> · Symboles : {preview.symbols.join(", ")}</span>}
          </div>
          {preview.warnings?.length > 0 && (
            <div className="mb-3 text-xs text-warn"><AlertTriangle size={12} className="inline mr-1" />{preview.warnings.join(" ")}</div>
          )}
          {preview.preview?.length > 0 && (
            <div className="overflow-x-auto mb-4">
              <table className="w-full text-[11.5px]" data-testid="preview-table">
                <thead>
                  <tr className="text-left text-white/40 uppercase text-[10px] tracking-widest border-b border-white/10">
                    <th className="py-1.5 pr-3 font-medium">Symbole</th>
                    <th className="py-1.5 pr-3 font-medium">Sens</th>
                    <th className="py-1.5 pr-3 font-medium">Volume</th>
                    <th className="py-1.5 pr-3 font-medium">Entrée</th>
                    <th className="py-1.5 pr-3 font-medium">Sortie</th>
                    <th className="py-1.5 pr-3 font-medium">P&L</th>
                    <th className="py-1.5 font-medium">Ouverture</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {preview.preview.map((t, i) => (
                    <tr key={i} className="border-b border-white/5 last:border-0">
                      <td className="py-1.5 pr-3 font-semibold">{t.symbol}</td>
                      <td className={`py-1.5 pr-3 ${t.side === "buy" ? "text-up" : t.side === "sell" ? "text-down" : "text-white/50"}`}>{t.side || "—"}</td>
                      <td className="py-1.5 pr-3">{t.volume ?? "—"}</td>
                      <td className="py-1.5 pr-3">{t.entry ?? "—"}</td>
                      <td className="py-1.5 pr-3">{t.exit ?? "—"}</td>
                      <td className={`py-1.5 pr-3 ${t.pnl >= 0 ? "text-up" : "text-down"}`}>{t.pnl}</td>
                      <td className="py-1.5 text-white/50">{t.open_time || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.n_trades > preview.preview.length && (
                <div className="text-[10px] text-white/40 mt-1">…et {preview.n_trades - preview.preview.length} autre(s) trade(s)</div>
              )}
            </div>
          )}
          <div className="flex gap-2">
            {preview.n_trades > 0 && (
              <button className="btn btn-primary" onClick={doImport} disabled={importing} data-testid="import-confirm-btn">
                {importing ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Confirmer l&apos;import ({preview.n_trades} trades)
              </button>
            )}
            <button className="btn" onClick={() => setPreview(null)} data-testid="import-cancel-btn">Annuler</button>
          </div>
        </div>
      )}

      {/* Import result */}
      {result && (
        <div className="panel p-5 border border-up/30" data-testid="import-result">
          <div className="flex items-center gap-2 text-up font-semibold text-sm mb-2">
            <ShieldCheck size={16} /> {result.n_trades} trades importés — format détecté : {result.format}
          </div>
          <div className="text-xs text-white/60 space-y-1">
            <div>Symboles : {result.symbols.join(", ")}</div>
            <div className="font-mono">SHA-256 du fichier : {result.content_sha256?.slice(0, 32)}…</div>
            <div className="font-mono">Scellé dans la chaîne d&apos;audit : {result.audit_event_hash?.slice(0, 32)}…</div>
          </div>
          {(() => {
            const nt = result.skipped?.non_trade || 0;
            const inv = result.skipped?.invalid || 0;
            if (nt + inv === 0) return (
              <div className="mt-2 text-xs text-up" data-testid="import-skipped-none">
                ✓ Toutes les lignes de données ({result.n_rows ?? result.n_trades}) ont été importées — aucune ligne ignorée.
              </div>
            );
            return (
              <div className="mt-2 p-2 bg-warn/10 border border-warn/30 rounded text-xs text-warn" data-testid="import-skipped-summary">
                <AlertTriangle size={12} className="inline mr-1" />
                <b>{nt + inv} ligne(s) ignorée(s)</b> sur {result.n_rows} lignes de données :
                {nt > 0 && <span> {nt} non-trade (balance, dépôt, retrait…)</span>}
                {nt > 0 && inv > 0 && " · "}
                {inv > 0 && <span> {inv} invalide(s) (symbole ou profit manquant / non numérique)</span>}
              </div>
            );
          })()}
          {result.warnings?.length > 0 && (
            <div className="mt-2 text-xs text-warn"><AlertTriangle size={12} className="inline mr-1" />{result.warnings.join(" ")}</div>
          )}
        </div>
      )}

      {/* Existing imports + report generation */}
      {imported && imported.total > 0 && (
        <div className="panel p-5" data-testid="imported-summary">
          <div className="flex flex-wrap justify-between items-start gap-3">
            <div>
              <div className="panel-title"><FileText size={14} className="inline mr-2" />Vos trades importés</div>
              <div className="text-sm mt-2 text-white/70">
                {imported.total} trades — {Object.entries(imported.per_symbol).map(([s, n]) => `${s} (${n})`).join(", ")}
              </div>
              {quota && quota.limit !== -1 && (
                <div className="text-xs text-white/50 mt-1" data-testid="report-quota-info">
                  Quota Free : {quota.used}/{quota.limit} rapport ce mois-ci
                </div>
              )}
              {Object.values(imported.per_symbol).some((n) => n < 100) && (
                <div className="mt-2 p-2 bg-warn/10 border border-warn/30 rounded text-xs text-warn" data-testid="import-sample-warning">
                  <AlertTriangle size={12} className="inline mr-1" />
                  Certains symboles ont moins de 100 trades — la puissance statistique sera limitée (indiqué dans le rapport).
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button className="btn btn-primary" onClick={downloadReport} disabled={pdfLoading} data-testid="generate-report-btn">
                {pdfLoading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Générer mon rapport (PDF)
              </button>
              <button className="btn text-down border-down/30" onClick={clearAll} data-testid="clear-imports-btn">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export guides */}
      <div className="panel p-5" data-testid="export-guides">
        <div className="panel-title mb-3">Comment exporter votre historique ?</div>
        {GUIDES.map((g, i) => (
          <div key={g.platform} className="border-b border-white/5 last:border-0">
            <button className="w-full flex justify-between items-center py-3 text-sm text-left hover:text-accent-blue transition-colors"
                    onClick={() => setOpenGuide(openGuide === i ? null : i)} data-testid={`guide-toggle-${i}`}>
              <span className="font-semibold">{g.platform}</span>
              <ChevronDown size={14} className={`transition-transform ${openGuide === i ? "rotate-180" : ""}`} />
            </button>
            {openGuide === i && (
              <ol className="pb-3 pl-4 space-y-1.5 text-xs text-white/60 list-decimal">
                {g.steps.map((s) => <li key={s}>{s}</li>)}
              </ol>
            )}
          </div>
        ))}
      </div>

      <p className="text-xs text-white/40">
        Vos trades importés sont <b>auto-déclarés</b> : leur intégrité est scellée par hash-chain à l&apos;import
        (SHA-256 du fichier + événement d&apos;audit chaîné), mais leur origine n&apos;est pas certifiée par un tiers.
        Le rapport PDF l&apos;indique explicitement. La synchronisation broker en lecture seule (badge « Vérifié ») est en développement.
      </p>
    </div>
  );
}
